/**
 * useEditSystems hook
 */

// React
import { useCallback, useState } from 'react';

// Libraries
import {
  Control,
  FieldArrayWithId,
  FieldErrors,
  useFieldArray,
  useForm,
  UseFormHandleSubmit,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import * as yup from 'yup';
import { useResetRecoilState, useSetRecoilState } from 'recoil';
import { useNavigate } from 'react-router-dom';

// Utils
import {
  FORMAT_DATE,
  NOTIFICATION_MESSAGES,
  StatusKey,
  VALIDATION_MESSAGES,
} from 'utils/constants';
import { DataEditSystem } from 'utils/types';
import { defaultRadioIsUsed } from 'utils/helper/common';

// Recoil
import { notifyAtom } from 'recoil/NotifyRecoil';
import { loadingAtom } from 'recoil/LoadingRecoil';
import { paramsGetListAtom } from 'recoil/ParamsGetListRecoil';

// Model
import { IUserServiceUpdateRequest } from 'models/UserRequest';

// Router
import ROUTES from 'configs/route';

// Services
import UserService from 'services/UserService';

// Interface
interface FormDateEditSystem {
  systems: DataEditSystem[];
}

interface UseEditSystemsType {
  control: Control<FormDateEditSystem, any>;
  fields: FieldArrayWithId<FormDateEditSystem, 'systems', 'id'>[];
  modalConfirm: boolean;
  setValue: UseFormSetValue<FormDateEditSystem>;
  errors: FieldErrors<FormDateEditSystem>;
  handleSubmit: UseFormHandleSubmit<FormDateEditSystem, undefined>;
  handleConfirmUpdate: () => void;
  handleCloseModalUpdate: () => void;
  handleUpdate: (_userId: number) => Promise<void>;
  handleUsageChange: (_index: number, _value: number) => void;
  watch: UseFormWatch<FormDateEditSystem>;
  trigger: UseFormTrigger<FormDateEditSystem>;
  fetchData: () => Promise<void>;
}

// Custom validate
const { START_DATE, END_DATE } = VALIDATION_MESSAGES;

const schema = yup.object().shape({
  systems: yup.array().of(
    yup.object().shape({
      created_at: yup.date().nullable(),
      start_date: yup.date().when(['is_used', 'isApproved'], {
        is: (is_used: number, isApprove: boolean) => is_used === 1 && !isApprove,
        then: (schema) =>
          schema
            .required(START_DATE.REQUIRED)
            .typeError(START_DATE.INVALID)
            .min(yup.ref('created_at'), START_DATE.MIN_VALUE)
            .test('createdAtOrToday', START_DATE.MIN_CURRENT_TIME, function (value) {
              const { created_at } = this.parent;
              const currentDate = dayjs().startOf('day');
              const valueDate = dayjs(value).startOf('day');
              if (created_at) {
                if (valueDate < dayjs(created_at).startOf('day')) {
                  return this.createError({ message: START_DATE.MIN_VALUE });
                }
              } else {
                if (valueDate < currentDate) {
                  return this.createError({ message: START_DATE.MIN_CURRENT_TIME });
                }
              }

              return true;
            }),
        otherwise: (schema) => schema.notRequired(),
      }),
      end_date: yup.date().when(['is_used', 'isApproved'], {
        is: (is_used: number, isApprove: boolean) => is_used === 1 && !isApprove,
        then: (schema) =>
          schema
            .required(END_DATE.REQUIRED)
            .typeError(END_DATE.INVALID)
            .min(yup.ref('start_date'), END_DATE.MIN_VALUE),
        otherwise: (schema) => schema.notRequired(),
      }),
    })
  ),
}) as any;

// Hook
export const useEditSystems = (userID: number): UseEditSystemsType => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    clearErrors,
    watch,
    trigger,
  } = useForm<FormDateEditSystem>({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    resolver: yupResolver<FormDateEditSystem>(schema),
  });

  const { fields } = useFieldArray({
    name: 'systems',
    control,
  });

  const navigate = useNavigate();
  const resetParamsGetUsers = useResetRecoilState(paramsGetListAtom);
  const [modalConfirm, setModalConfirm] = useState(false);
  const setIsLoading = useSetRecoilState(loadingAtom);
  const setNotify = useSetRecoilState(notifyAtom);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      await UserService.getListUserService(userID).then((res) => {
        res.forEach((item) => {
          if (item.is_used === null) {
            item.is_used = defaultRadioIsUsed(item.status);
          }
        });
        setValue('systems', res);
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userID, setValue]);

  const handleConfirmUpdate = () => {
    setModalConfirm(true);
  };

  const handleCloseModalUpdate = () => {
    setModalConfirm(false);
  };

  const handleUpdate = async (userID: number) => {
    const dataInput = watch('systems');
    setIsLoading(true);
    const dataFilterNotUse = dataInput.filter((item) => !(item.is_used === 0 && item.id === null));
    const dataUpdateUserService: IUserServiceUpdateRequest[] = [] as IUserServiceUpdateRequest[];
    dataFilterNotUse.forEach((item) => {
      if (item.isApproved) {
        item.status = StatusKey.CANCELED;
        item.is_used = 0;
        item.start_date = null;
        item.end_date = null;
      }
      dataUpdateUserService.push({
        id: item.id,
        service_id: item.service_id,
        is_used: item.is_used ?? defaultRadioIsUsed(item.status),
        status: item.status,
        start_date: item.start_date
          ? dayjs(item.start_date).format(FORMAT_DATE)?.toString()
          : undefined,
        end_date: item.end_date ? dayjs(item.end_date).format(FORMAT_DATE)?.toString() : undefined,
      });
    });
    try {
      await UserService.updateUserService(userID, dataUpdateUserService);
      setNotify({ open: true, title: NOTIFICATION_MESSAGES.SUCCESS.UPDATE });
    } catch (error) {
      setNotify({ open: true, severity: 'error', title: NOTIFICATION_MESSAGES.ERROR.UPDATE });
      console.error(error);
    } finally {
      resetParamsGetUsers();
      navigate(ROUTES.USER_INFO);
      setIsLoading(false);
      setModalConfirm(false);
    }
  };

  const handleUsageChange = (index: number, value: number) => {
    if (value === 0) {
      clearErrors(`systems`);
    }
    setValue(`systems.${index}.is_used`, value);
    const status = watch(`systems.${index}.status`);
    const startDate = watch(`systems.${index}.start_date`);
    const isApproved = watch(`systems.${index}.isApproved`);
    if ((status === StatusKey.CANCELED || status === StatusKey.FINISHED) && value === 1) {
      setValue(`systems.${index}.isApproved`, false);
      const formattedDate: string = dayjs(new Date()).format(FORMAT_DATE);
      setValue(`systems.${index}.created_at`, dayjs(formattedDate).toDate());
      setValue(`systems.${index}.start_date`, dayjs(formattedDate).toDate());
      setValue(`systems.${index}.end_date`, null);
    }
    if (value && !startDate && !isApproved) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      setValue(`systems.${index}.start_date`, today);
    }
  };

  return {
    control,
    fields,
    modalConfirm,
    errors,
    setValue,
    trigger,
    handleSubmit,
    handleConfirmUpdate,
    handleCloseModalUpdate,
    handleUpdate,
    handleUsageChange,
    watch,
    fetchData,
  };
};
